// Admin: find a device by Unique Number, and assign it to a user / location /
// room. The React equivalent of the old Windows "Devices" form.
//
// ── The assignment chain ───────────────────────────────────────────────────
// A device is not usable until FOUR things line up, and doing only the first is
// the classic half-assignment that leaves a device nobody can see:
//
//   1. DevicesInstalled row      MasterUserId, LocationID, RoomId, DeviceName
//   2. user -> location mapping  or the user cannot open the location
//   3. user -> room mapping      or the room is invisible inside it
//   4. user -> device mapping    or the device is invisible inside the room
//
// So `assignDevice` always writes all four, and treats "already mapped" as
// success rather than an error — the endpoints are additive and re-running an
// assignment is a normal thing for an admin to do.
//
// ⚠️ ROOM 0 MEANS "COMMON DEVICE". A device with RoomId 0 belongs to the
// location rather than to any room, and is what the app lists under Common
// Devices. It is a real value, not a missing one, so it must never be treated
// as "no room chosen yet".

import { createOrClaimLocation } from '../admin/locationPool';
import {
  post, getDeviceDetailFresh, getDeviceInstalledById, getLocations, getRooms,
  addLocation, addRoom, registerUser,
  addUserLocationMapping, addLocationRoomsMapping, addUserDeviceMapping,
  updateDeviceInstalled, getDevicesByRoom, getCommonDevices,
  deleteUserDeviceMapping,
} from '../api/api';

/** RoomId used for a device that belongs to the location, not to a room. */
export const COMMON_ROOM_ID = 0;

const rows = (res) => (res && Array.isArray(res.data) ? res.data : []);

/** Every user, for the Master User picker. 450+ rows, so filter in the UI. */
export const getAllUsers = () => post('GetAllUserDetails', {});

export const userLabel = (u) => {
  const name = [u.Surname, u.Name, u.FatherHusband].filter(Boolean).join(' ').trim();
  return name || `User ${u.UserId}`;
};

/**
 * ⚠️ A NAME IS NOT UNIQUE AND MUST NEVER BE USED AS A KEY. Measured against the
 * live list: 457 users share 432 labels — "biswas Pranay Nepal" is FIVE
 * different accounts, "Jumrani Yash Dinesh" another five.
 *
 * MUI's Autocomplete keys each option on `getOptionLabel(option)` unless it is
 * given something better (see getOptionProps in useAutocomplete), so a picker
 * that omits an explicit key hands React duplicate keys. React then cannot tell
 * those siblings apart between renders — it drops some and reuses others — and
 * the list re-rendered on every keystroke shows the same person repeatedly
 * while other people vanish from it. That is a rendering fault, not a search
 * one: the filter is returning the right rows.
 */
export const userOptionKey = (u) => String(u && u.UserId);

/**
 * Everything a user can be found by, for `tokenFilter`.
 *
 * One definition, because it was written out by hand in three pickers and they
 * had already drifted — the id was searchable in none of them, which is the
 * only thing that separates two people with the same name.
 */
export const userSearchText = (u) => [
  u.Surname || '', u.Name || '', u.FatherHusband || '',
  u.MobileNumber || '', u.UserId != null ? `id${u.UserId}` : '',
].join(' ');

/**
 * Look a device up by Unique Number.
 *
 * -> { found, device, assigned }
 *
 * `assigned` is the question the admin actually has: a row can EXIST (the
 * device was manufactured and registered) while being attached to no location,
 * which is a different state from "unknown number" and needs different wording.
 *
 * ⚠️ `getDeviceDetailFresh`, NEVER the local-first `getDeviceDetail`. On a
 * native build that cache is the SIGNED-IN user's sync, so for a device
 * belonging to somebody else — which is every device this page is opened for —
 * it answers with a row this admin's own app happens to hold, stale or absent.
 * LocationName / RoomName then disagree with the LocationID / RoomId the
 * assignment is built from, and the assignment is written against the wrong
 * "before".
 */
export async function findDevice(uniqueNumber) {
  const uno = String(uniqueNumber || '').trim();
  if (!uno) return { found: false, device: null, assigned: false };
  const device = rows(await getDeviceDetailFresh(uno).catch(() => null))[0] || null;
  if (!device) return { found: false, device: null, assigned: false };
  const locationId = device.LocationID ?? device.LocationId;
  return {
    found: true,
    device: { ...device, ...(await installExtras(device.ID)) },
    assigned: locationId != null && Number(locationId) > 0,
  };
}

/**
 * The three columns the detail query does not select, read from the raw
 * DevicesInstalled row.
 *
 * `WifiName` is the one that matters: it is the SSID the device is joined to
 * RIGHT NOW — the firmware sends it with every status update — and it is the
 * first thing anyone asks when a device has stopped answering. None of the
 * device-detail endpoints return it.
 *
 * ⚠️ NAMED FIELDS ONLY, never a spread of the row: it also carries
 * `WifiPassword` in clear, and this object ends up in component state.
 *
 * Best-effort — an extra call must not turn a working lookup into a failure, so
 * a miss yields `{}` and the caller simply has no SSID to show.
 */
async function installExtras(installedId) {
  if (installedId == null) return {};
  const row = rows(await getDeviceInstalledById(installedId).catch(() => null))[0];
  if (!row) return {};
  return {
    WifiName: row.WifiName || '',
    // When the device last spoke to the broker, as against LastUpdatedDateTime
    // which any write to the row moves.
    LastMqttPing: row.LastMqttPing || '',
    LastStatusTime: row.LastStatusTime || '',
  };
}

/**
 * Create a location for a user and map them to it. -> LocationID
 *
 * ⚠️ `MasterUserId` IS NOT OPTIONAL, and leaving it out did two things at once.
 * The payload used to be `{ LocationName }` alone, but AddLocation is an UPSERT
 * whose match is built by string concatenation:
 *
 *     where: "LocationName = '<name>' and MasterUserId = " + req.body.MasterUserId
 *
 * With the field absent that reads `MasterUserId = undefined` — not valid SQL,
 * so the "does it already exist" check could never match and every save made
 * ANOTHER location of the same name. And the row it then inserted had no master
 * at all, which is the thing that makes a location the user's own: it is what
 * `Location.MasterUserId` means, and what the reorder and edit screens read.
 *
 * ⚠️ SEND ONLY REAL `Location` COLUMNS. The insert branch writes `req.body`
 * wholesale, so a stray `UserId` — which that table does not have — fails the
 * whole statement. The mapping is a separate call, below.
 */
export async function createLocation(userId, locationName, address) {
  /*
   * ⚠️ A FREE ROW IS REUSED BEFORE ANYTHING IS INSERTED. A third of the table
   * is empty — 38 of 121 — and every insert widened that. The pool hands back a
   * SCRUBBED row (address, mobile, Wi-Fi and every stale mapping cleared), so
   * nothing of the previous occupant is inherited; see core/admin/locationPool.
   *
   * It falls through to the insert below whenever the pool is empty or a claim
   * loses a race, so this can never fail to produce a location.
   */
  const pooled = await createOrClaimLocation(userId, locationName, address)
    .catch(() => null);
  if (pooled && pooled.locationId != null) return pooled.locationId;

  const a = address || {};
  const text = (v) => String(v ?? '').trim();
  const res = await addLocation({
    LocationName: locationName,
    MasterUserId: Number(userId),
    /*
     * ⚠️ THE COLUMN NAMES ARE THE SHOUTY ONES, and they are not negotiable:
     * `Location` has ADD1 / ADD2 / CITY / PINCODE. The insert writes this
     * object straight into the table, so `Address1` or `Pincode` is not a
     * near-miss that gets normalised — it is a column that does not exist and
     * the whole statement fails.
     *
     * Empty strings rather than omitted keys: a location created without an
     * address should hold '' like every other row does, not NULL, because the
     * mobile room list prints ADD1 as its subtitle and renders a null as the
     * word "null".
     */
    ADD1: text(a.add1),
    ADD2: text(a.add2),
    CITY: text(a.city),
    PINCODE: text(a.pincode),
  });
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) throw new Error('The server did not return the new location id.');
  await addUserLocationMapping(userId, id).catch(() => {});
  return id;
}

/**
 * Create a user, for the "this device belongs to somebody new" case. -> UserId
 *
 * ⚠️ `AddNewUser` IS AN UPSERT KEYED ON MobileNumber. If the number already
 * exists it does not fail and it does not create anything — it UPDATES that
 * person, overwriting their name and PIN with what was typed here. A mistyped
 * digit therefore rewrites a real customer's account and locks them out, with a
 * success response either way.
 *
 * The only honest signal is the reply shape: an insert returns `insertId`, an
 * update does not. So a reply without one is treated as "that number is already
 * somebody" and raised, never reported as success. The caller checks the loaded
 * user list first as well — this is the backstop, not the only guard.
 *
 * Password is the 4-digit PIN the login screen asks for; CompanyInfoType decides
 * the theme and the company the user belongs to, so it is inherited from the
 * admin doing the assigning rather than left null.
 */
export async function createUser({
  surname, name, fatherHusband, mobileNumber, password, companyInfoType,
}) {
  const mobile = String(mobileNumber || '').replace(/\D/g, '');
  if (mobile.length !== 10) throw new Error('The mobile number must be 10 digits.');
  if (!String(name || '').trim() && !String(surname || '').trim()) {
    throw new Error('The user needs a name.');
  }
  const res = await registerUser({
    Surname: String(surname || '').trim(),
    Name: String(name || '').trim(),
    FatherHusband: String(fatherHusband || '').trim(),
    MobileNumber: mobile,
    Password: String(password || '').trim(),
    CompanyInfoType: companyInfoType != null ? Number(companyInfoType) : 1,
  });
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) {
    throw new Error(`${mobile} already belongs to a user — pick them from the list instead.`);
  }
  return id;
}

/** Create a room in a location and map the user to it. -> RoomID */
export async function createRoom(userId, locationId, roomName) {
  const res = await addRoom(roomName, locationId);
  const id = res?.data?.insertId ?? res?.data?.[0]?.insertId;
  if (id == null) throw new Error('The server did not return the new room id.');
  await addLocationRoomsMapping(userId, locationId, id).catch(() => {});
  return id;
}

export const locationsOf = async (userId) => rows(await getLocations(userId).catch(() => null));
export const roomsOf = async (userId, locationId) =>
  rows(await getRooms(userId, locationId).catch(() => null));

/**
 * The devices in one room — or, for COMMON_ROOM_ID, the location's common
 * devices, which live in a different table and need a different call. Selecting
 * "Common devices" in a room dropdown must not fetch room 0.
 *
 * The location goes to the room call as well: without it the room endpoint hides
 * whole device types, and this page's whole job is to explain where a device is
 * — showing a room that omits the very device being looked up is the worst
 * possible answer here. See core/devices/roomMerge.js.
 */
export async function devicesIn(userId, locationId, roomId) {
  if (Number(roomId) === COMMON_ROOM_ID) {
    return rows(await getCommonDevices(userId, locationId).catch(() => null));
  }
  return rows(await getDevicesByRoom(userId, roomId, locationId).catch(() => null));
}

/**
 * Write the install row and every mapping the user needs to actually see it.
 *
 * @param device        the row from findDevice (for DeviceInstalledId)
 * @param roomId        a RoomID, or COMMON_ROOM_ID for a location-level device
 *
 * ⚠️ Writes MasterUserId. The row ALSO carries DeviceMasterUserId and
 * LocationMasterUserId, which the old form exposes as separate fields; those are
 * left alone rather than guessed at, since setting the wrong one silently moves
 * ownership.
 */
export async function assignDevice({
  device, uniqueNumber, userId, locationId, roomId, deviceName,
}) {
  const data = {
    MasterUserId: Number(userId),
    LocationID: Number(locationId),
    RoomId: Number(roomId),
  };
  if (deviceName != null && String(deviceName).trim()) {
    data.DeviceName = String(deviceName).trim();
  }
  await updateDeviceInstalled(uniqueNumber, data);

  // The three mappings. Each is additive and may already exist, so a rejection
  // here is not a failure of the assignment — the row above is what matters.
  await addUserLocationMapping(userId, locationId).catch(() => {});
  if (Number(roomId) !== COMMON_ROOM_ID) {
    await addLocationRoomsMapping(userId, locationId, roomId).catch(() => {});
  }
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId != null) {
    await addUserDeviceMapping(userId, installedId).catch(() => {});
  }
  return true;
}

/** Change the device's type (DeviceID on the install row). */
export const setDeviceType = (uniqueNumber, deviceId) =>
  updateDeviceInstalled(uniqueNumber, { DeviceID: Number(deviceId) });

/**
 * Detach a device from its owner, location and room.
 *
 * ⚠️ THE MIRROR OF assignDevice, AND IT HAS TO UNDO ALL OF IT. Clearing only
 * MasterUserId (what the app's own "remove device" does) leaves LocationID and
 * RoomId pointing at a site the device no longer belongs to, and leaves the
 * user->device mapping in place — so the device keeps appearing in that room
 * for that user while reporting no owner. Half-unassigned is harder to diagnose
 * than either state.
 *
 * The location and room mappings are deliberately NOT removed: they belong to
 * the user's own access to that site and other devices may still rely on them.
 */
export async function unassignDeviceFully(device, uniqueNumber) {
  await updateDeviceInstalled(uniqueNumber, {
    MasterUserId: 0,
    LocationID: 0,
    RoomId: 0,
  });
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  const owner = device?.MasterUserId ?? device?.DeviceMasterUserId;
  if (installedId != null && owner != null) {
    await deleteUserDeviceMapping(installedId, owner).catch(() => {});
  }
  return true;
}

// ── Who can see this device ────────────────────────────────────────────────
// GetAllUsersByUniqueNumber returns UserIds and nothing else, so names are
// resolved against the full user list the page already holds.
export async function deviceUserIds(uniqueNumber) {
  const list = rows(await post('GetAllUsersByUniqueNumber', { UniqueNumber: uniqueNumber })
    .catch(() => null));
  return list.map((r) => Number(r.UserId)).filter((n) => Number.isFinite(n));
}

/**
 * Give a user access to this device.
 *
 * ⚠️ THE DEVICE MAPPING ALONE IS NOT ACCESS. A user who is mapped to the device
 * but not to its location cannot open the location, so the room never renders
 * and the device is invisible — the mapping exists and the user still cannot
 * see the thing. Location and room go with it, every time.
 *
 * A common device (RoomId 0) has no room to map, and asking for room 0 would
 * write a mapping to a room that does not exist.
 */
export async function grantUserAccess(device, userId) {
  const locationId = device?.LocationID ?? device?.LocationId;
  const roomId = device?.RoomId;
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId == null) throw new Error('This device has no installed id.');

  if (locationId != null && Number(locationId) > 0) {
    await addUserLocationMapping(userId, locationId).catch(() => {});
    if (roomId != null && Number(roomId) !== COMMON_ROOM_ID) {
      await addLocationRoomsMapping(userId, locationId, roomId).catch(() => {});
    }
  }
  await addUserDeviceMapping(userId, installedId);
  return true;
}

/**
 * Take a user's access to this device away.
 *
 * Only the DEVICE mapping is removed. Their location and room mappings are left
 * alone deliberately: those are their access to the whole site, and other
 * devices in that room almost certainly depend on them — removing them here
 * would silently empty rooms that have nothing to do with this device.
 */
export async function revokeUserAccess(device, userId) {
  const installedId = device?.ID ?? device?.DeviceInstalledID ?? device?.DeviceInstalledId;
  if (installedId == null) throw new Error('This device has no installed id.');
  await deleteUserDeviceMapping(installedId, userId);
  return true;
}
